import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Editor } from '@tinymce/tinymce-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import AuthService from "../../../api/services/AuthService";
import { alertSuccessMessage, alertErrorMessage } from "../../../customComponent/CustomAlertMessage";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";
import { ApiConfig } from "../../../api/apiConfig/ApiConfig";
import DataTableBase from "../../../customComponent/DataTable";

const tinymceApiKey = process.env.REACT_APP_TINY_API || 'your-tinymce-api-key';

const BlogCategory = () => {
  const navigate = useNavigate();
  const [bannerList, setBannerList] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedYoutubeUrl, setSelectedYoutubeUrl] = useState('');
  
  // Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [blogLink, setBlogLink] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [blogMedia, setBlogMedia] = useState(null);
  const [content, setContent] = useState('');
  const [existingImage, setExistingImage] = useState('');
  
  // SEO Fields
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [slug, setSlug] = useState('');
  
  // Image cropping states
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: 2 / 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    handleBanners();
  }, []);

  const handleBanners = async () => {
    LoaderHelper.loaderStatus(true);
    await AuthService.getBlogCategories().then(async result => {
      if (result.success) {
        LoaderHelper.loaderStatus(false);
        try {
          setBannerList(result.data);
        } catch (error) {
          alertErrorMessage(error);
        }
      } else {
        LoaderHelper.loaderStatus(false);
        alertErrorMessage("No data found");
      }
    });
  }

  const deleteBanner = async (userId) => {
    LoaderHelper.loaderStatus(true);
    await AuthService.deleteBlog(userId).then(async result => {
      LoaderHelper.loaderStatus(false);
      if (result.success) {
        alertSuccessMessage(result.message);
        handleBanners();
      } else {
        alertErrorMessage(result.message);
      }
    });
  }

  const handleStatus = async (userId, cell) => {
    await AuthService.blogStatus(userId, cell).then(async result => {
      if (result.success) {
        handleBanners();
      } else {
        alertErrorMessage(result.message);
      }
    });
  }

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*$/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleYoutubeClick = (youtubeUrl) => {
    setSelectedYoutubeUrl(youtubeUrl);
    setShowYoutubeModal(true);
  };

  const handleUpdateBlog = (row) => {
    setEditingBlog(row);
    setCategory(row.category || '');
    setTitle(row.title || '');
    setBlogLink(row.blogLink || '');
    setYoutubeUrl(row.youtubeUrl || '');
    setContent(row.content || '');
    setExistingImage(row.blogMedia || '');
    setPreviewUrl(row.blogMedia ? ApiConfig?.appUrl + row.blogMedia : '');
    setBlogMedia(null);
    
    // SEO Fields
    setMetaTitle(row.metaTitle || '');
    setMetaDescription(row.metaDescription || '');
    setMetaKeywords(row.metaKeywords || '');
    setSlug(row.slug || '');
    
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingBlog(null);
    setCategory('');
    setTitle('');
    setBlogLink('');
    setYoutubeUrl('');
    setContent('');
    setBlogMedia(null);
    setPreviewUrl('');
    setExistingImage('');
    setImgSrc('');
    setCrop({ unit: '%', width: 100, aspect: 2 / 1 });
    setCompletedCrop(null);
    
    // Reset SEO Fields
    setMetaTitle('');
    setMetaDescription('');
    setMetaKeywords('');
    setSlug('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      if (!allowedImageTypes.includes(file.type)) {
        alertErrorMessage("Only image files (JPEG, PNG, GIF, WEBP) are allowed for blog media");
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setShowCropModal(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
    const defaultCrop = {
      unit: '%',
      width: 100,
      height: 50,
      x: 0,
      y: 25,
      aspect: 2 / 1
    };
    setCrop(defaultCrop);
    setCompletedCrop({
      unit: '%',
      width: 100,
      height: 50,
      x: 0,
      y: 25
    });
  };

  const getCroppedImg = async () => {
    if (!imgRef.current) {
      alertErrorMessage("Image not loaded properly");
      return;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    const cropToUse = completedCrop || crop;
    
    canvas.width = cropToUse.width;
    canvas.height = cropToUse.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      cropToUse.x * scaleX,
      cropToUse.y * scaleY,
      cropToUse.width * scaleX,
      cropToUse.height * scaleY,
      0,
      0,
      cropToUse.width,
      cropToUse.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          alertErrorMessage('Canvas is empty');
          return;
        }
        const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
        resolve(file);
      }, 'image/jpeg', 1);
    });
  };

  const handleCropComplete = async () => {
    const croppedImage = await getCroppedImg();
    if (croppedImage) {
      setBlogMedia(croppedImage);
      setPreviewUrl(URL.createObjectURL(croppedImage));
      setShowCropModal(false);
      setImgSrc('');
      alertSuccessMessage("Image cropped successfully");
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImgSrc('');
    setCrop({ unit: '%', width: 100, aspect: 2 / 1 });
    setCompletedCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpdateSubmit = async () => {
    // Validate that at least one media type is provided
    if (!blogMedia && !existingImage && !youtubeUrl) {
      alertErrorMessage("Please provide either a blog image or YouTube URL");
      return;
    }

    // Validate blog link format if provided
    const blogLinkRegex = /^(https?:\/\/)[\w\-]+(\.[\w\-]+)+[/#?]?.*$/;
    if (blogLink && !blogLinkRegex.test(blogLink)) {
      alertErrorMessage("Please enter a valid blog link (e.g., https://example.com)");
      return;
    }

    // Validate YouTube URL format if provided
    if (youtubeUrl && !youtubeUrl.includes('youtube.com') && !youtubeUrl.includes('youtu.be')) {
      alertErrorMessage("Please enter a valid YouTube URL");
      return;
    }

    var formData = new FormData();
    formData.append('category', category);
    formData.append('title', title);
    formData.append('content', content);
    formData.append('blogLink', blogLink);
    formData.append('youtubeUrl', youtubeUrl);
    
    // SEO Fields
    formData.append('metaTitle', metaTitle);
    formData.append('metaDescription', metaDescription);
    formData.append('metaKeywords', metaKeywords);
    formData.append('slug', slug);
    
    if (blogMedia) {
      formData.append('blogMedia', blogMedia);
    }

    LoaderHelper.loaderStatus(true);
    // You need to create this method in AuthService
    await AuthService.updateBlog(editingBlog._id, formData).then(async result => {
      LoaderHelper.loaderStatus(false);
      if (result.success) {
        try {
          alertSuccessMessage(result.message);
          handleCloseEditModal();
          handleBanners();
        } catch (error) {
          alertErrorMessage(error);
        }
      } else {
        const errorMessage = result.message;
        alertErrorMessage(errorMessage);
      }
    });
  };

  const linkFollow = (row) => {
    return (
      <div className="d-flex gap-1">
        <button 
          className="btn btn-primary btn-sm" 
          type="button" 
          onClick={() => handleUpdateBlog(row)}
          title="Edit Blog"
        >
          <i className="fa fa-edit"></i>
        </button>
        <button 
          className="btn btn-danger btn-sm" 
          type="button" 
          onClick={() => deleteBanner(row?._id)}
          title="Delete Blog"
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
    );
  };

  const statuslinkFollow = (row) => {
    return (
      <>
        <button 
          type="button" 
          className={row?.status === "ACTIVE" ? "btn btn-sm btn-primary" : "btn btn-sm btn-danger"} 
          style={{ marginLeft: "20px" }} 
          onClick={() => handleStatus(row?._id, row?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE")}
        >
          {row?.status}
        </button>
      </>
    );
  };

  function imageFormatter(row) {
    if (row?.youtubeUrl) {
      return (
        <button 
          type="button"
          className="btn btn-link p-0" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleYoutubeClick(row?.youtubeUrl);
          }}
          title="View YouTube Video"
        >
          <i className="fab fa-youtube" style={{ fontSize: '32px', color: '#FF0000' }}></i>
        </button>
      );
    }
    if (row?.blogMedia) {
      return (
        <img 
          className="table-img" 
          src={ApiConfig?.appUrl + row?.blogMedia} 
          alt="Blog Media" 
          style={{ cursor: 'pointer', width: '80px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
          onClick={() => handleImageClick(ApiConfig?.appUrl + row?.blogMedia)}
          title="Click to view full image"
        />
      );
    }
    return "---";
  }

  function blogImageFormatter(row) {
    if (row?.blogMedia) {
      return (
        <img 
          className="table-img" 
          src={ApiConfig?.appUrl + row?.blogMedia} 
          alt="Blog Image" 
          style={{ cursor: 'pointer', width: '80px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
          onClick={() => handleImageClick(ApiConfig?.appUrl + row?.blogMedia)}
          title="Click to view full image"
        />
      );
    }
    return "---";
  }

  const columns = [
    { name: "Image", shrink: true, selector: row => blogImageFormatter(row) },
    { name: "Type", shrink: true, selector: row => row.category },
    { name: "Title", shrink: true, wrap: true, selector: row => row.title },
    { name: "External Link", shrink: true, wrap: true, selector: row => row.blogLink ? <a href={row.blogLink} target="_blank" rel="noreferrer">View Link</a> : "---" },
    { name: "Media", shrink: true, selector: row => imageFormatter(row) },
    { name: "Status", shrink: true, selector: statuslinkFollow },
    { name: "Action", shrink: true, selector: linkFollow },
  ];

  return (
    <>
      <div id="layoutSidenav_content">
        <main>
          <header className="page-header page-header-dark bg-gradient-primary-to-secondary pb-10">
            <div className="container-xl px-4">
              <div className="page-header-content pt-4">
                <div className="row align-items-center justify-content-between">
                  <div className="col-auto mt-4">
                    <h1 className="page-header-title">
                      <div className="page-header-icon"><i className="fa fa-image"></i></div>
                      Blogs Management
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </header>
          <div className="container-xl px-4 mt-n10">
            <div className="card">
              <div className="card-header">
                Blog List
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-12">
                    <div className="table-responsive">
                      <DataTableBase columns={columns} data={bannerList} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Blog Image</h5>
                <button type="button" className="btn-close" onClick={() => setShowImageModal(false)}></button>
              </div>
              <div className="modal-body text-center">
                <img 
                  src={selectedImage} 
                  alt="Blog" 
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImageModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* YouTube Modal */}
      {showYoutubeModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">YouTube Video</h5>
                <button type="button" className="btn-close" onClick={() => setShowYoutubeModal(false)}></button>
              </div>
              <div className="modal-body">
                {selectedYoutubeUrl && getYouTubeVideoId(selectedYoutubeUrl) && (
                  <div className="ratio ratio-16x9">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedYoutubeUrl)}?autoplay=1`}
                      title="YouTube video"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    ></iframe>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowYoutubeModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Blog Modal */}
      {showEditModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, overflow: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '90vw' }}>
            <div className="modal-content ">
              <div className="modal-header px-4">
                <h5 className="modal-title">Edit Blog</h5>
                <button type="button" className="btn-close" onClick={handleCloseEditModal}></button>
              </div>
              <div className="modal-body p-4" style={{ maxHeight: '75vh', overflow: 'auto' }}>
                <form>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3 form-group">
                        <label className="small mb-1">Category <span className="text-danger">*</span></label>
                        <select
                          className="form-control form-control-solid form-select"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                        >
                          <option value="" hidden>Select Blog Category</option>
                          <option value="News">News</option>
                          <option value="Analysis">Analysis</option>
                          <option value="Events">Events</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="small mb-1">Blog Title <span className="text-danger">*</span></label>
                        <input
                          className="form-control form-control-solid"
                          type="text"
                          placeholder="Enter Blog Title"
                          value={title}
                          onChange={(e) => {
                            const newTitle = e.target.value;
                            setTitle(newTitle);
                            // Auto-generate slug from title
                            const autoSlug = newTitle
                              .toLowerCase()
                              .trim()
                              .replace(/[^a-z0-9\s-]/g, '')
                              .replace(/\s+/g, '-')
                              .replace(/-+/g, '-');
                            setSlug(autoSlug);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="small mb-1">External Blog Link (Optional)</label>
                        <input
                          className="form-control form-control-solid"
                          type="text"
                          placeholder="https://example.com/blog"
                          value={blogLink}
                          onChange={(e) => setBlogLink(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="small mb-1">YouTube URL (Optional)</label>
                        <input
                          className="form-control form-control-solid"
                          type="text"
                          placeholder="https://youtube.com/watch?v=..."
                          value={youtubeUrl}
                          onChange={(e) => setYoutubeUrl(e.target.value)}
                        />
                        <small className="text-muted">Provide YouTube URL or upload an image</small>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="small mb-1">
                          Wall Image {!youtubeUrl && !existingImage && <span className="text-danger">*</span>}
                          <small className="text-danger ms-1">(Aspect Ratio: 2:1)</small>
                        </label>
                        <div>
                          <input
                            ref={fileInputRef}
                            className="d-none"
                            type="file"
                            onChange={onSelectFile}
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          />
                          {!previewUrl ? (
                            <button
                              type="button"
                              className="btn btn-primary mb-2"
                              onClick={handleButtonClick}
                            >
                              <i className="fa fa-upload me-2"></i>
                              Upload Blog Wall Image
                            </button>
                          ) : (
                            <div>
                              <p className="small mb-2 fw-bold">Preview:</p>
                              <div className="position-relative d-inline-block">
                                <img
                                  src={previewUrl}
                                  alt="Preview"
                                  style={{ maxWidth: '100%', maxHeight: '150px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                              </div>
                              <div className="mt-2">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm me-2"
                                  onClick={() => {
                                    setBlogMedia(null);
                                    setPreviewUrl('');
                                    setExistingImage('');
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = '';
                                    }
                                  }}
                                >
                                  <i className="fa fa-trash me-1"></i>
                                  Remove
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  onClick={handleButtonClick}
                                >
                                  <i className="fa fa-sync me-1"></i>
                                  Change
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <small className="text-muted">Supported: JPEG, JPG, PNG, GIF, WEBP</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      {youtubeUrl && getYouTubeVideoId(youtubeUrl) && (
                        <div>
                          <p className="small mb-2 fw-bold">YouTube Preview:</p>
                          <div className="ratio ratio-16x9" style={{ maxWidth: '100%' }}>
                            <iframe
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(youtubeUrl)}`}
                              title="YouTube video preview"
                              allowFullScreen
                              style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                            ></iframe>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="small mb-1">Blog Content <span className="text-danger">*</span></label>
                    <Editor
                      apiKey={tinymceApiKey}
                      onInit={(evt, editor) => editorRef.current = editor}
                      value={content}
                      onEditorChange={(newContent) => setContent(newContent)}
                      init={{
                        height: 300,
                        menubar: true,
                        plugins: [
                          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                          'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | blocks | ' +
                          'bold italic forecolor | alignleft aligncenter ' +
                          'alignright alignjustify | bullist numlist outdent indent | ' +
                          'removeformat | help',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                      }}
                    />
                  </div>

                  {/* SEO Section */}
                  <div className="border-top pt-3 mt-3">
                    <h6 className="mb-3">SEO Details</h6>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="small mb-1">SEO Meta Title</label>
                          <input
                            className="form-control form-control-solid"
                            type="text"
                            placeholder="Enter SEO meta title"
                            value={metaTitle}
                            onChange={(e) => setMetaTitle(e.target.value)}
                            maxLength="60"
                          />
                          <small className="text-muted">{metaTitle.length}/60 characters</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="small mb-1">URL Slug</label>
                          <input
                            className="form-control form-control-solid"
                            type="text"
                            placeholder="blog-url-slug"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                          />
                          <small className="text-muted">Used for SEO-friendly URLs</small>
                        </div>
                      </div>
                    </div>

                    <div className="form-group mb-3">
                      <label className="small mb-1">SEO Meta Description</label>
                      <textarea
                        className="form-control form-control-solid"
                        rows="3"
                        placeholder="Enter SEO meta description"
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        maxLength="160"
                      ></textarea>
                      <small className="text-muted">{metaDescription.length}/160 characters</small>
                    </div>

                    <div className="form-group mb-3">
                      <label className="small mb-1">Meta Keywords</label>
                      <input
                        className="form-control form-control-solid"
                        type="text"
                        placeholder="keyword1, keyword2, keyword3"
                        value={metaKeywords}
                        onChange={(e) => setMetaKeywords(e.target.value)}
                      />
                      <small className="text-muted">Separate keywords with commas</small>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseEditModal}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleUpdateSubmit}
                  disabled={!category || !title || !content || (!blogMedia && !existingImage && !youtubeUrl)}
                >
                  Update Blog
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Crop Modal */}
      {showCropModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '90vw' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Crop Blog Wall Image</h5>
                <button type="button" className="btn-close" onClick={handleCropCancel}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {imgSrc && (
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={2 / 1}
                      style={{ maxWidth: '100%' }}
                    >
                      <img
                        ref={imgRef}
                        alt="Crop preview"
                        src={imgSrc}
                        onLoad={onImageLoad}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '60vh',
                          width: 'auto',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    </ReactCrop>
                  </div>
                )}
                <small className="text-muted d-block mt-2 text-center" style={{ position: 'absolute', bottom: '60px' }}>
                  Drag to adjust the crop area. The image will be cropped to a 2:1 aspect ratio.
                </small>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCropCancel}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleCropComplete}>
                  <i className="fa fa-check me-2"></i>OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BlogCategory;
